import type {
  RuntimeTreeInstance,
  VilniusTreeFamily,
} from "../world/schema/runtimeTile.ts";

export const TREE_VISUAL_ARCHETYPES = [
  "linden",
  "maple",
  "birch",
  "oak",
  "pine",
] as const;

export type TreeVisualArchetype =
  (typeof TREE_VISUAL_ARCHETYPES)[number];

export type TreeVisualMaterialFamily =
  | "deciduous"
  | "birch"
  | "evergreen";

export interface TreeCrownLayerProfile {
  readonly offsetX: number;
  readonly offsetZ: number;
  readonly heightRatio: number;
  readonly widthScale: number;
  readonly verticalScale: number;
  readonly depthScale: number;
}

export interface TreeVisualProfile {
  readonly crownShape: "faceted" | "conical";
  readonly materialFamily: TreeVisualMaterialFamily;
  readonly trunkRadiusToCrownRatio: number;
  readonly trunkHeightRatio: number;
  readonly branchCount: number;
  readonly branchLengthScale: number;
  readonly mediumLodCrownLayerCount: number;
  readonly springBlossomFrequency: number;
  readonly crownLayers: readonly Readonly<TreeCrownLayerProfile>[];
}

export interface TreePlacementProfile {
  readonly family: VilniusTreeFamily;
  readonly spacingM: number;
  readonly minimumRoadClearanceM: number;
  readonly minimumBuildingClearanceM: number;
  readonly minimumHeightM: number;
  readonly maximumHeightM: number;
  readonly minimumCrownRadiusM: number;
  readonly maximumCrownRadiusM: number;
}

export interface StreetTreePlacementProfile {
  readonly family: VilniusTreeFamily;
  readonly spacingM: number;
  readonly offsetFromCurbM: number;
  readonly minimumBuildingClearanceM: number;
  readonly minimumHeightM: number;
  readonly maximumHeightM: number;
  readonly minimumCrownRadiusM: number;
  readonly maximumCrownRadiusM: number;
  /** Stable city-wide projection axis used to keep clipped tile rows aligned. */
  readonly samplingAxis: Readonly<{ x: number; z: number }>;
}

/**
 * Official GRPK land-cover classes used as tree-placement evidence.
 *
 * - ms0: forest
 * - sd15: trees, shrubs, planted or naturally regenerated vegetation
 *
 * The polygons prove vegetated coverage, not individual surveyed stems.
 * Positions inside them are deterministic authored sampling.
 */
export const TREE_PLACEMENT_BY_GRPK_CLASS:
  Readonly<Record<string, Readonly<TreePlacementProfile>>> = Object.freeze({
    ms0: Object.freeze({
      family: "mixed_woodland",
      spacingM: 24,
      minimumRoadClearanceM: 4.5,
      minimumBuildingClearanceM: 2,
      minimumHeightM: 8.5,
      maximumHeightM: 14.5,
      minimumCrownRadiusM: 2.7,
      maximumCrownRadiusM: 4.2,
    }),
    sd15: Object.freeze({
      family: "deciduous_planting",
      spacingM: 15,
      minimumRoadClearanceM: 3.5,
      minimumBuildingClearanceM: 1.5,
      minimumHeightM: 6.5,
      maximumHeightM: 11.5,
      minimumCrownRadiusM: 2.1,
      maximumCrownRadiusM: 3.5,
    }),
  });

export const TREE_RENDER_CONFIG = Object.freeze({
  trunkRadiusToCrownRatio: 0.11,
  trunkHeightRatio: 0.55,
  lowerCrownHeightRatio: 0.69,
  upperCrownHeightRatio: 0.84,
  nearCrownVerticalScale: 0.78,
  farCrownVerticalScale: 0.9,
  minimumVisibleTreeHeightM: 2,
  crownYawVariationRadians: 0.24,
  crownWidthVariation: 0.09,
  crownDepthVariation: 0.08,
  distantCrownScale: 1.02,
});

/**
 * Reference-led silhouettes, not claims that any individual generated stem is
 * a surveyed botanical specimen. Vilnius' published inventory identifies
 * maple, linden, birch and pine as the city's most numerous tree groups, while
 * the oak adds the broad, low crown seen in established parkland.
 */
export const TREE_VISUAL_PROFILES: Readonly<
  Record<TreeVisualArchetype, Readonly<TreeVisualProfile>>
> = Object.freeze({
  linden: Object.freeze({
    crownShape: "faceted",
    materialFamily: "deciduous",
    trunkRadiusToCrownRatio: 0.1,
    trunkHeightRatio: 0.56,
    branchCount: 2,
    branchLengthScale: 0.7,
    mediumLodCrownLayerCount: 2,
    springBlossomFrequency: 7,
    crownLayers: Object.freeze([
      Object.freeze({
        offsetX: -0.06,
        offsetZ: 0.05,
        heightRatio: 0.72,
        widthScale: 0.98,
        verticalScale: 0.72,
        depthScale: 0.94,
      }),
      Object.freeze({
        offsetX: 0.16,
        offsetZ: -0.12,
        heightRatio: 0.86,
        widthScale: 0.74,
        verticalScale: 0.58,
        depthScale: 0.72,
      }),
    ]),
  }),
  maple: Object.freeze({
    crownShape: "faceted",
    materialFamily: "deciduous",
    trunkRadiusToCrownRatio: 0.105,
    trunkHeightRatio: 0.52,
    branchCount: 3,
    branchLengthScale: 0.82,
    mediumLodCrownLayerCount: 2,
    springBlossomFrequency: 9,
    crownLayers: Object.freeze([
      Object.freeze({
        offsetX: -0.2,
        offsetZ: 0.08,
        heightRatio: 0.7,
        widthScale: 0.88,
        verticalScale: 0.66,
        depthScale: 0.84,
      }),
      Object.freeze({
        offsetX: 0.24,
        offsetZ: -0.08,
        heightRatio: 0.76,
        widthScale: 0.82,
        verticalScale: 0.63,
        depthScale: 0.82,
      }),
      Object.freeze({
        offsetX: 0.02,
        offsetZ: 0.04,
        heightRatio: 0.88,
        widthScale: 0.68,
        verticalScale: 0.57,
        depthScale: 0.66,
      }),
    ]),
  }),
  birch: Object.freeze({
    crownShape: "faceted",
    materialFamily: "birch",
    trunkRadiusToCrownRatio: 0.065,
    trunkHeightRatio: 0.67,
    branchCount: 2,
    branchLengthScale: 0.58,
    mediumLodCrownLayerCount: 2,
    springBlossomFrequency: 0,
    crownLayers: Object.freeze([
      Object.freeze({
        offsetX: -0.16,
        offsetZ: 0.06,
        heightRatio: 0.72,
        widthScale: 0.66,
        verticalScale: 0.88,
        depthScale: 0.62,
      }),
      Object.freeze({
        offsetX: 0.2,
        offsetZ: -0.1,
        heightRatio: 0.82,
        widthScale: 0.58,
        verticalScale: 0.78,
        depthScale: 0.56,
      }),
      Object.freeze({
        offsetX: 0,
        offsetZ: 0.02,
        heightRatio: 0.91,
        widthScale: 0.46,
        verticalScale: 0.62,
        depthScale: 0.44,
      }),
    ]),
  }),
  oak: Object.freeze({
    crownShape: "faceted",
    materialFamily: "deciduous",
    trunkRadiusToCrownRatio: 0.15,
    trunkHeightRatio: 0.43,
    branchCount: 4,
    branchLengthScale: 0.95,
    mediumLodCrownLayerCount: 2,
    springBlossomFrequency: 0,
    crownLayers: Object.freeze([
      Object.freeze({
        offsetX: -0.24,
        offsetZ: 0.1,
        heightRatio: 0.62,
        widthScale: 1.08,
        verticalScale: 0.58,
        depthScale: 0.98,
      }),
      Object.freeze({
        offsetX: 0.32,
        offsetZ: -0.1,
        heightRatio: 0.66,
        widthScale: 0.94,
        verticalScale: 0.56,
        depthScale: 0.9,
      }),
      Object.freeze({
        offsetX: -0.08,
        offsetZ: -0.28,
        heightRatio: 0.73,
        widthScale: 0.88,
        verticalScale: 0.54,
        depthScale: 0.84,
      }),
      Object.freeze({
        offsetX: 0.06,
        offsetZ: 0.06,
        heightRatio: 0.82,
        widthScale: 0.72,
        verticalScale: 0.5,
        depthScale: 0.68,
      }),
    ]),
  }),
  pine: Object.freeze({
    crownShape: "conical",
    materialFamily: "evergreen",
    trunkRadiusToCrownRatio: 0.075,
    trunkHeightRatio: 0.63,
    branchCount: 2,
    branchLengthScale: 0.68,
    mediumLodCrownLayerCount: 2,
    springBlossomFrequency: 0,
    crownLayers: Object.freeze([
      Object.freeze({
        offsetX: -0.08,
        offsetZ: 0.08,
        heightRatio: 0.72,
        widthScale: 0.9,
        verticalScale: 1.16,
        depthScale: 0.86,
      }),
      Object.freeze({
        offsetX: 0.12,
        offsetZ: -0.08,
        heightRatio: 0.83,
        widthScale: 0.68,
        verticalScale: 0.96,
        depthScale: 0.66,
      }),
      Object.freeze({
        offsetX: -0.02,
        offsetZ: 0.02,
        heightRatio: 0.92,
        widthScale: 0.46,
        verticalScale: 0.72,
        depthScale: 0.44,
      }),
    ]),
  }),
});

type TreeVisualEvidence = Pick<
  RuntimeTreeInstance,
  "id" | "family" | "placement" | "speciesCode"
>;

function stableTreeHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function treeVisualVariation(
  treeId: string,
  salt: string,
): number {
  return stableTreeHash(`${treeId}:${salt}`) / 0x1_0000_0000;
}

export function treeVisualArchetypeFor(
  tree: Readonly<TreeVisualEvidence>,
): TreeVisualArchetype {
  if (tree.placement === "official-municipal-tree-inventory") {
    const sourceArchetype = municipalSpeciesArchetype(tree.speciesCode);
    if (sourceArchetype !== null) {
      return sourceArchetype;
    }
  }
  const percentile = Math.floor(treeVisualVariation(tree.id, "archetype") * 100);
  if (tree.placement === "reference-derived-street-row") {
    return percentile < 82 ? "linden" : "maple";
  }
  if (tree.family === "mixed_woodland") {
    return percentile < 38
      ? "pine"
      : percentile < 62
        ? "birch"
        : percentile < 80
          ? "oak"
          : percentile < 92
            ? "maple"
            : "linden";
  }
  return percentile < 29
    ? "maple"
    : percentile < 56
      ? "linden"
      : percentile < 76
        ? "birch"
        : percentile < 92
          ? "oak"
          : "pine";
}

function municipalSpeciesArchetype(
  code: number | undefined,
): TreeVisualArchetype | null {
  if (code === undefined) {
    return null;
  }
  if ([1, 80, 99, 114, 115, 116, 201].includes(code)) {
    return "linden";
  }
  if ([2, 27, 106, 202].includes(code)) {
    return "birch";
  }
  if ([3, 4, 89, 90, 91, 92, 93, 169, 203].includes(code)) {
    return "maple";
  }
  if ([11, 24, 117, 170, 205].includes(code)) {
    return "oak";
  }
  if ([
    8, 9, 10, 26, 28, 29, 30, 55, 56, 57, 83, 84, 85, 86, 103, 107,
    118, 119, 120, 139, 153, 206, 207, 219, 220, 221,
  ].includes(code)) {
    return "pine";
  }
  return null;
}

/**
 * Reference-derived street-tree rows where GRPK proves the road alignment but
 * its vegetation polygons do not encode the individual planted stems visible
 * at street level. These remain explicitly labelled authored placements.
 */
export const STREET_TREE_PLACEMENT_BY_STREET_NAME: Readonly<
  Record<string, Readonly<StreetTreePlacementProfile>>
> = Object.freeze({
  "gedimino pr.": Object.freeze({
    family: "deciduous_planting",
    spacingM: 13,
    offsetFromCurbM: 1.75,
    minimumBuildingClearanceM: 1.25,
    minimumHeightM: 7.5,
    maximumHeightM: 10.5,
    minimumCrownRadiusM: 2.15,
    maximumCrownRadiusM: 3.05,
    samplingAxis: Object.freeze({ x: 0.964, z: 0.266 }),
  }),
});

export function treePlacementProfileForGrpkClass(
  classCode: string,
): Readonly<TreePlacementProfile> | null {
  return TREE_PLACEMENT_BY_GRPK_CLASS[classCode] ?? null;
}

export function streetTreePlacementProfileForName(
  name: string | undefined,
): Readonly<StreetTreePlacementProfile> | null {
  if (name === undefined) {
    return null;
  }
  return STREET_TREE_PLACEMENT_BY_STREET_NAME[
    name.trim().toLocaleLowerCase("lt-LT")
  ] ?? null;
}
